import { gql } from '@apollo/client';
import { Box, Stack, Text } from '@chakra-ui/react';
import { t } from 'i18next';
import { useCallback, useState } from 'react';
import { ConfigureAdminRolesModal_UserFragment } from '~components/organization/AdminProfilesTable/__generated__/ConfigureAdminRolesModal.graphql';
import { AdminProfilesTable_UserFragment } from '~components/organization/AdminProfilesTable/__generated__/useAdminProfilesTable.graphql';
import { AdminProfilesTable } from '~components/organization/AdminProfilesTable/AdminProfilesTable';
import { ConfigureAdminRolesModal } from '~components/organization/AdminProfilesTable/ConfigureAdminRolesModal';
import { SearchUserModal } from '~components/organization/AdminProfilesTable/SearchUserModal';
import { useAdminProfilesTable } from '~components/organization/AdminProfilesTable/useAdminProfilesTable';
import { VerticalTabContent } from '~components/ui/VerticalTabs';
import { useAdminProfilesIndexPageQuery } from './__generated__/Index.graphql';

const DEFAULT_PAGE_SIZE = 25;

export function OrganizationAdminProfilesIndexPage() {
  const [search, setSearch] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [isSearchUserModalOpen, setIsSearchUserModalOpen] = useState(false);
  const [configuringUser, setConfiguringUser] =
    useState<ConfigureAdminRolesModal_UserFragment | null>(null);
  const [configureMode, setConfigureMode] = useState<'create' | 'edit'>('create');

  const { data, loading, error, refetch } = useAdminProfilesIndexPageQuery({
    variables: { search, pageSize, pageNumber: pageIndex + 1 },
  });

  const users = data?.serviceAdminUsers.users ?? [];
  const totalRowCount = data?.serviceAdminUsers.totalCount ?? 0;

  const handleSetSearch = (value: string) => {
    setPageIndex(0);
    setSearch(value);
  };

  const handleEdit = useCallback((user: AdminProfilesTable_UserFragment) => {
    setConfigureMode('edit');
    setConfiguringUser(user);
  }, []);

  const handleSelectEligibleUser = (user: ConfigureAdminRolesModal_UserFragment) => {
    setIsSearchUserModalOpen(false);
    setConfigureMode('create');
    setConfiguringUser(user);
  };

  const handleConfigureCancel = () => {
    setConfiguringUser(null);
  };

  const handleConfigureSuccess = () => {
    setConfiguringUser(null);
    setIsSearchUserModalOpen(false);
    void refetch();
  };

  const handleRevoked = useCallback(() => void refetch(), [refetch]);

  const table = useAdminProfilesTable({
    data: users,
    pageIndex,
    pageSize,
    totalRowCount,
    onEdit: handleEdit,
    onRevoked: handleRevoked,
  });

  return (
    <VerticalTabContent
      title={t('adminProfiles')}
      isLoading={loading}
      error={error}
      action={{ label: t('addAdmin'), onClick: () => setIsSearchUserModalOpen(true) }}
    >
      <Text color="gray.600" marginBottom="6">
        {t('adminProfilesDescription')}
      </Text>
      <Stack direction={{ sm: 'column', md: 'row' }} spacing="8" marginBottom="4">
        <AdminProfilesTable.SearchBar search={search} handleSetSearch={handleSetSearch} />
      </Stack>
      <Box
        boxShadow="elevated"
        border="1px solid"
        borderRadius="md"
        borderColor="gray.100"
        overflowX="auto"
        bgColor="white"
      >
        <AdminProfilesTable table={table} />
      </Box>
      <Box display="flex" marginTop="4">
        <Box flex="1">
          <AdminProfilesTable.PageSizeSelector
            pageSize={pageSize}
            handleSetPageSize={(size) => {
              setPageSize(size);
              setPageIndex(0);
            }}
          />
        </Box>
        <AdminProfilesTable.Pagination
          pageIndex={pageIndex}
          totalRowCount={totalRowCount}
          pageSize={pageSize}
          handleSetPageIndex={setPageIndex}
        />
      </Box>

      <SearchUserModal
        isOpen={isSearchUserModalOpen}
        onCancel={() => setIsSearchUserModalOpen(false)}
        onSelectUser={handleSelectEligibleUser}
      />

      {configuringUser && (
        <ConfigureAdminRolesModal
          key={configuringUser.id}
          user={configuringUser}
          mode={configureMode}
          onCancel={handleConfigureCancel}
          onSuccess={handleConfigureSuccess}
        />
      )}
    </VerticalTabContent>
  );
}

gql`
  query AdminProfilesIndexPage($search: String, $pageSize: Int, $pageNumber: Int) {
    serviceAdminUsers(search: $search, pageSize: $pageSize, pageNumber: $pageNumber) {
      totalCount
      users {
        ...AdminProfilesTable_user
      }
    }
  }
`;
